import React, { useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import { X } from 'lucide-react';
import data from '../data/chords.json';

export interface Variation { id: string; capo: number; shape: string; frets: number[] }
interface Chord { root: string; quality: string; intervals: number[]; variations: Variation[] }
export const chordLibrary: Record<string, Chord> = data.chords;
const flats: Record<string, string> = {
  Db: "C#",
  Eb: "D#",
  Gb: "F#",
  Ab: "G#",
  Bb: "A#",
  Cb: "B",
  Fb: "E",
};

export function normalizeChord(name: string): string {
  if (!name) return "";
  let normalized = name.replace(/♯/g, "#").replace(/♭/g, "b");
  normalized = normalized.replace(/^[A-G]b/, (root) => flats[root] || root);
  normalized = normalized.replace(/\/([A-G]b)/, (_, bass) => "/" + (flats[bass] || bass));
  return normalized;
}

export const commonSlashChords: Record<string, Variation[]> = {
  "D/F#": [
    { id: "D/F#-0-thumb", capo: 0, shape: "D/F#", frets: [2, -1, 0, 2, 3, 2] },
    { id: "D/F#-0-full", capo: 0, shape: "D/F#", frets: [2, 0, 0, 2, 3, 2] },
  ],
  "G/B": [
    { id: "G/B-0", capo: 0, shape: "G/B", frets: [-1, 2, 0, 0, 3, 3] },
    { id: "G/B-0-open", capo: 0, shape: "G/B", frets: [-1, 2, 0, 0, 0, 3] },
  ],
  "C/G": [
    { id: "C/G-0", capo: 0, shape: "C/G", frets: [3, 3, 2, 0, 1, 0] },
  ],
  "Am/G": [
    { id: "Am/G-0", capo: 0, shape: "Am/G", frets: [3, 0, 2, 2, 1, 0] },
    { id: "Am/G-0-mute", capo: 0, shape: "Am/G", frets: [3, -1, 2, 2, 1, 0] },
  ],
  "F/A": [
    { id: "F/A-0", capo: 0, shape: "F/A", frets: [-1, 0, 3, 2, 1, 1] },
  ],
  "A/C#": [
    { id: "A/C#-0", capo: 0, shape: "A/C#", frets: [-1, 4, 2, 2, 2, 0] },
  ],
  "E/G#": [
    { id: "E/G#-0", capo: 0, shape: "E/G#", frets: [4, 2, 2, 1, 0, 0] },
  ],
};

export interface ResolvedChordInfo {
  originalName: string;
  normalizedName: string;
  baseChord: string;
  bassNote?: string;
  resolvedChord: string;
  chordData: Chord;
  isSlash: boolean;
  isSimplified: boolean;
  slashVariations?: Variation[];
}

export function generateSimplificationCandidates(chord: string): string[] {
  const candidates: string[] = [];
  const clean = chord.replace(/[()]/g, "");

  const withoutSlashTension = clean.replace(/\/(?:[0-9]+|maj[0-9]*|min[0-9]*|m[0-9]*).*$/, "");
  if (withoutSlashTension && withoutSlashTension !== clean) {
    candidates.push(withoutSlashTension);
  }

  const withoutAlts = (withoutSlashTension || clean).replace(/(?:#11|b13|#9|b9|#5|b5)/g, "");
  if (withoutAlts && !candidates.includes(withoutAlts)) {
    candidates.push(withoutAlts);
  }

  const withoutHighDegrees = withoutAlts.replace(/(?:11|13)/g, "7");
  if (withoutHighDegrees && !candidates.includes(withoutHighDegrees)) {
    candidates.push(withoutHighDegrees);
  }

  const rootMatch = clean.match(/^([A-G][#b]?)(.*)$/);
  if (rootMatch) {
    const root = rootMatch[1];
    const rest = rootMatch[2];

    const isMinor = rest.startsWith("m") && !rest.startsWith("maj");
    if (isMinor) {
      if (!candidates.includes(`${root}madd9`)) candidates.push(`${root}madd9`);
      if (!candidates.includes(`${root}m7`)) candidates.push(`${root}m7`);
      if (!candidates.includes(`${root}m`)) candidates.push(`${root}m`);
    } else {
      if (rest.includes("maj")) {
        if (!candidates.includes(`${root}maj7`)) candidates.push(`${root}maj7`);
      } else if (rest.includes("sus")) {
        if (!candidates.includes(`${root}sus4`)) candidates.push(`${root}sus4`);
        if (!candidates.includes(`${root}sus2`)) candidates.push(`${root}sus2`);
      } else if (rest.includes("add")) {
        if (!candidates.includes(`${root}add9`)) candidates.push(`${root}add9`);
      } else if (rest.includes("7")) {
        if (!candidates.includes(`${root}7`)) candidates.push(`${root}7`);
      }
      if (!candidates.includes(root)) candidates.push(root);
    }
  }

  return candidates;
}

export function resolveChord(name: string): ResolvedChordInfo | null {
  if (!name) return null;
  const normalized = normalizeChord(name.trim());

  if (chordLibrary[normalized]) {
    return {
      originalName: name,
      normalizedName: normalized,
      baseChord: normalized,
      resolvedChord: normalized,
      chordData: chordLibrary[normalized],
      isSlash: false,
      isSimplified: false,
    };
  }

  let basePart = normalized;
  let bassNote: string | undefined = undefined;

  const slashMatch = normalized.match(/^(.*?)\/([A-G][#b]?(?:m|min|maj)?)$/);
  if (slashMatch) {
    basePart = slashMatch[1];
    bassNote = slashMatch[2];
  }

  const slashVars = commonSlashChords[normalized];

  let resolvedChord = basePart;
  let chordData = chordLibrary[resolvedChord];

  if (!chordData) {
    const candidates = generateSimplificationCandidates(basePart);
    for (const cand of candidates) {
      if (chordLibrary[cand]) {
        resolvedChord = cand;
        chordData = chordLibrary[cand];
        break;
      }
    }
  }

  if (!chordData) {
    return null;
  }

  return {
    originalName: name,
    normalizedName: normalized,
    baseChord: basePart,
    bassNote,
    resolvedChord,
    chordData,
    isSlash: Boolean(bassNote),
    isSimplified: resolvedChord !== basePart,
    slashVariations: slashVars,
  };
}

export function ChordDiagram({ variation }: { variation: Variation }) {
  const positive = variation.frets.filter(f=>f>0);
  const start = Math.max(0,...positive)<=5 ? 1 : Math.min(...positive);
  const label = variation.frets.map(f=>f<0 ? 'muted' : f===0 ? 'open' : `fret ${f}`).join(', ');
  return <svg viewBox="0 0 240 260" width="240" style={{ maxWidth:'100%' }} role="img" aria-label={`${variation.shape}, ${label}, capo ${variation.capo}`}>
    {Array.from({length:6},(_,i)=><line key={`s${i}`} x1={45+i*30} x2={45+i*30} y1={48} y2={228} stroke="#475569" strokeWidth="1.5" />)}
    {Array.from({length:6},(_,i)=><line key={`f${i}`} x1={45} x2={195} y1={48+i*36} y2={48+i*36} stroke="#475569" strokeWidth={i===0 && start===1 ? 5 : 1.5} />)}
    <text x="15" y="72" fill="#64748b" fontSize="14">{start}fr</text>
    {variation.frets.map((f,i)=><g key={i}>
      <text x={45+i*30} y="32" textAnchor="middle" fontSize="18" fill="#334155">{f<0?'×':f===0?'○':''}</text>
      {f>0 && <circle cx={45+i*30} cy={48+(f-start+0.5)*36} r="11" fill="#7c3aed" />}
      <text x={45+i*30} y="250" textAnchor="middle" fontSize="13" fill="#64748b">{data.tuning[i]}</text>
    </g>)}
  </svg>;
}

export function CustomChordDisplay({ name, custom }: { name: string; custom: { frets: number[]; capo?: number; shape?: string } }) {
  const variation: Variation = {
    id: "custom",
    capo: custom.capo || 0,
    shape: custom.shape || name,
    frets: custom.frets,
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography variant="h4" fontWeight={800} color="#7c3aed">
          {name}
        </Typography>
        <Chip
          label="Custom Fingering"
          size="small"
          sx={{ bgcolor: "#f5f3ff", color: "#7c3aed", fontWeight: 700, border: "1px solid #ddd6fe" }}
        />
      </Stack>
      <Box textAlign="center" my={2}>
        <ChordDiagram variation={variation} />
      </Box>
      <Typography textAlign="center" fontWeight={600}>
        User-defined custom fingering
      </Typography>
      <Typography textAlign="center" color="text.secondary" variant="body2" mt={1}>
        Frets from low E to high E: {variation.frets.map((f) => (f < 0 ? "×" : f)).join(" · ")}
      </Typography>
      <Typography textAlign="center" color="text.secondary" variant="caption" display="block" mt={0.5}>
        {variation.capo ? `Capo ${variation.capo}` : "○ Open string · × Do not play · Purple dots mark fretted notes."}
      </Typography>
    </Box>
  );
}

export function ChordVariations({ name, customChords }: { name: string; customChords?: Record<string, any> }) {
  const [capo, setCapo] = useState(0);
  const [index, setIndex] = useState(0);

  const custom = customChords && (customChords[name] || customChords[normalizeChord(name)]);
  if (custom && Array.isArray(custom.frets)) {
    return <CustomChordDisplay name={name} custom={custom} />;
  }

  const resolved = resolveChord(name);
  if (!resolved) return <Typography>No diagram available for {name} yet.</Typography>;

  let variations: Variation[] = [];
  if (capo === 0 && resolved.slashVariations && resolved.slashVariations.length > 0) {
    variations = resolved.slashVariations;
  } else {
    variations = resolved.chordData.variations.filter((v) => v.capo === capo);
  }

  const selected = variations[index] || variations[0];

  let subtitle = "";
  if (resolved.isSlash && resolved.isSimplified) {
    subtitle = `Base: ${resolved.baseChord} (showing ${resolved.resolvedChord} shape) · Bass Note: ${resolved.bassNote}`;
  } else if (resolved.isSlash) {
    subtitle = `Base: ${resolved.baseChord} · Bass Note: ${resolved.bassNote}`;
  } else if (resolved.isSimplified) {
    subtitle = `Showing ${resolved.resolvedChord} shape`;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={800}>{name}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Select
          size="small"
          value={capo}
          inputProps={{ 'aria-label': 'Capo' }}
          onChange={(e) => { setCapo(Number(e.target.value)); setIndex(0); }}
          MenuProps={{
            container: () => (typeof document !== 'undefined' ? (document.fullscreenElement as HTMLElement) || document.body : null),
          }}
        >
          {Array.from({ length: 8 }, (_, n) => (
            <MenuItem key={n} value={n}>{n === 0 ? 'No capo' : `Capo ${n}`}</MenuItem>
          ))}
        </Select>
      </Stack>
      {selected ? (
        <>
          <Box textAlign="center" my={2}><ChordDiagram variation={selected} /></Box>
          <Typography textAlign="center" fontWeight={600}>Play {selected.shape} shape · Sounds {name}</Typography>
          <Typography textAlign="center" color="text.secondary" variant="body2" mt={1}>
            Frets from low E to high E: {selected.frets.map((f) => f < 0 ? '×' : f).join(' · ')}
          </Typography>
          <Typography textAlign="center" color="text.secondary" variant="caption" display="block">
            {capo ? 'Fret numbers are relative to the capo; open strings rest at the capo.' : '○ Open string · × Do not play · Blue dots mark fretted notes.'}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
            <Button disabled={index === 0} onClick={() => setIndex(index - 1)}>Previous</Button>
            <Typography variant="body2">{index + 1} / {variations.length}</Typography>
            <Button disabled={index >= variations.length - 1} onClick={() => setIndex(index + 1)}>Next</Button>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" mt={2}>
            {variations.map((v, i) => (
              <Chip key={v.id} label={i + 1} clickable color={i === index ? 'primary' : 'default'} aria-label={`Variation ${i + 1}`} onClick={() => setIndex(i)} />
            ))}
          </Stack>
        </>
      ) : (
        <Typography my={3}>No voicings available at this capo.</Typography>
      )}
    </Box>
  );
}

export default function ChordViewer({
  name,
  customChords,
  onClose,
}: {
  name: string | null;
  customChords?: Record<string, any>;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={Boolean(name)}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="chord-dialog-title"
      container={() => (typeof document !== 'undefined' ? (document.fullscreenElement as HTMLElement) || document.body : null)}
    >
      <DialogTitle id="chord-dialog-title">
        Chord diagram
        <IconButton aria-label="Close chord diagram" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>{name && <ChordVariations key={name} name={name} customChords={customChords} />}</DialogContent>
    </Dialog>
  );
}
